import { markReachable, markUnreachable } from '../sync/connectivity';

export class ApiError extends Error {
  constructor(message: string, readonly status: number, readonly data: Record<string, unknown> = {}) { super(message); }
}

/** The request never reached the server (no reception, DNS failure, timeout). */
export class NetworkError extends Error {}

type UnauthorizedHandler = () => void;
let onUnauthorized: UnauthorizedHandler | null = null;
/** Called when an authenticated request is rejected because the session has expired. */
export function setUnauthorizedHandler(handler: UnauthorizedHandler | null) { onUnauthorized = handler; }

export interface RequestOptions {
  method?: string;
  body?: unknown;
  headers?: Record<string, string>;
  timeoutMs?: number;
  /** Set false for requests where 401 is an expected answer (sign-in) rather than an expired session. */
  sessionRequired?: boolean;
}

export async function request(path: string, options: RequestOptions = {}): Promise<Response> {
  const { method = 'GET', body, headers = {}, timeoutMs = 20000, sessionRequired = true } = options;
  const isJson = body !== undefined && !(body instanceof Blob) && !(body instanceof ArrayBuffer) && typeof body !== 'string';
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), timeoutMs);
  let response: Response;
  try {
    response = await fetch(path, {
      method,
      credentials: 'same-origin',
      headers: isJson ? { 'Content-Type': 'application/json', ...headers } : headers,
      body: isJson ? JSON.stringify(body) : (body as BodyInit | undefined),
      signal: controller.signal
    });
  } catch (error) {
    markUnreachable();
    throw new NetworkError(controller.signal.aborted ? 'The server did not respond in time.' : 'No connection to the server.', { cause: error });
  } finally {
    clearTimeout(timer);
  }
  markReachable();
  if (!response.ok) {
    const data = await response.json().catch(() => ({})) as Record<string, unknown>;
    if (response.status === 401 && sessionRequired) onUnauthorized?.();
    throw new ApiError(typeof data.error === 'string' ? data.error : `Request failed (${response.status}).`, response.status, data);
  }
  return response;
}

export async function api<T>(path: string, options: RequestOptions = {}): Promise<T> {
  return (await request(path, options)).json() as Promise<T>;
}
